import {
  column,
  dateColumn,
  GridColumn,
  GridTableLayout,
  numericColumn,
  PageHeaderLayout,
  RowStyles,
  simpleDataRows,
  SimpleHeaderAndData,
  useGridTableLayoutState,
} from "@homebound/beam";
import { useCallback, useMemo } from "react";
import { dateCell, priceCell } from "src/components";
import {
  mapToProjectStagesFilter,
  SingleStageFilter,
  useProjectStageSingleFilter,
} from "src/hooks/useProjectStageSingleFilter";
import { useRouteParams } from "src/hooks/useRouteParams";
import { useStripStage } from "src/hooks/useStripStage";
import { useProjectContext } from "src/routes/projects/context/ProjectContext";
import {
  ProjectEstimatesPageEstimateFragment,
  ProjectEstimatesPageQuery,
  useProjectEstimatesPageQuery,
} from "src/routes/projects/estimates/ProjectEstimatesPage.generated";
import { ProjectParams } from "src/routes/routesDef";
import { createEstimateUrl } from "src/RouteUrls";

export function ProjectEstimatesPage() {
  useStripStage();
  const { projectId } = useRouteParams<ProjectParams>();
  const { clientNoun, latestActiveStage, name } = useProjectContext();
  const { filterDefs } = useProjectStageSingleFilter(latestActiveStage);
  const layoutState = useGridTableLayoutState<SingleStageFilter>({
    persistedFilter: { filterDefs, storageKey: "estimatesFilter" },
  });

  const query = useProjectEstimatesPageQuery({
    variables: { projectId, stagesFilter: mapToProjectStagesFilter(layoutState.filter) },
  });

  const columns = useMemo(() => createColumns(clientNoun), [clientNoun]);
  const rowStyles = useMemo(() => createRowStyles(projectId), [projectId]);
  const createRows = useCallback(
    (data: ProjectEstimatesPageQuery | undefined) => simpleDataRows(data?.project.stages.flatMap((s) => s.estimates)),
    [],
  );

  return (
    <PageHeaderLayout pageHeader={{ title: "Estimates", documentTitleSuffix: name }}>
      <GridTableLayout
        layoutState={layoutState}
        tableProps={{ query, createRows, columns, rowStyles, emptyFallback: "No estimates found" }}
      />
    </PageHeaderLayout>
  );
}

type Row = SimpleHeaderAndData<ProjectEstimatesPageEstimateFragment>;

function createColumns(clientNoun: string): GridColumn<Row>[] {
  return [
    column<Row>({
      id: "estimate",
      name: "Estimate",
      canHide: false,
      header: "Estimate",
      mw: "180px",
      data: ({ title }) => title,
    }),
    column<Row>({
      id: "stage",
      name: "Stage",
      header: "Stage",
      mw: "184px",
      data: ({ projectStage }) => projectStage.stage.name,
    }),
    column<Row>({ id: "status", name: "Status", header: "Status", mw: "120px", data: ({ status }) => status.name }),
    column<Row>({
      id: "contractType",
      name: "Contract Type",
      header: "Contract Type",
      mw: "172px",
      data: ({ changeEvent }) => (changeEvent ? "H/O Change Order" : "Prime Contract"),
    }),
    column<Row>({ id: "type", name: "Type", header: "Type", mw: "120px", data: ({ type }) => type.name }),
    numericColumn<Row>({
      id: "estimatedCost",
      name: "Estimated Cost",
      header: "Estimated Cost",
      mw: "140px",
      data: ({ totalCostInCents }) => priceCell({ valueInCents: totalCostInCents }),
    }),
    numericColumn<Row>({
      id: "markup",
      name: "Markup",
      header: "Markup",
      mw: "120px",
      data: ({ totalMarkupInCents }) => priceCell({ valueInCents: totalMarkupInCents }),
    }),
    numericColumn<Row>({
      id: "price",
      name: `${clientNoun} Price`,
      header: `${clientNoun} Price`,
      mw: "140px",
      data: ({ totalPriceInCents }) => priceCell({ valueInCents: totalPriceInCents }),
    }),
    dateColumn<Row>({
      id: "lastUpdated",
      name: "Last Updated",
      header: "Last Updated",
      mw: "140px",
      data: ({ updatedAt }) => dateCell(updatedAt),
    }),
  ];
}

function createRowStyles(projectId: string): RowStyles<Row> {
  return { header: {}, data: { rowLink: ({ id }) => createEstimateUrl(projectId, id) } };
}
